import { useState, useContext, useEffect } from 'react';
import { AuthContext } from '../../AuthProvider';
import Axios from '../../Axios';
import Navbar2 from '../../navbar/Navbar2';
import BottomNavbar from '../../components/bottomNavbar/BottomNavbar';
import { Card, CardContent, CardHeader } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { Plus, AlertCircle, ChevronDown, X, Edit, Trash2 } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Badge } from '../../components/ui/badge';
import { ProfilePageContext } from '../../context/ProfilePageContext';
import ProfileCompleteCard from '../account-details/components/ProfileCompleteCard';
import './whatsapp-template.css';

function WhatsAppTemplate() {
  const { userToken } = useContext(AuthContext);
  const { getUserProfileData, userData, accountPercentage, fbpagePercentage, businessPercentage, notificationPercentage, overallPercentage } = useContext(ProfilePageContext);
  
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Form fields
  const [templateName, setTemplateName] = useState('');
  const [templateCategory, setTemplateCategory] = useState('MARKETING');
  const [templateLanguage, setTemplateLanguage] = useState('en_US');
  const [templateContent, setTemplateContent] = useState('');
  const [formError, setFormError] = useState('');

  // Template categories and languages options
  const templateCategories = [
    { value: 'MARKETING', label: 'Marketing' },
    { value: 'UTILITY', label: 'Utility' },
    // { value: 'AUTHENTICATION', label: 'Authentication' }
  ];

  const languages = [
    { value: 'en_US', label: 'English (US)' },
    { value: 'en', label: 'English' },
    { value: 'hi_IN', label: 'Hindi' },
    { value: 'es_ES', label: 'Spanish' }
  ];

  // Fetch WhatsApp templates on component mount
  useEffect(() => {
    if (userToken) {
      getTemplates();
      getUserProfileData(userToken);
    }
  }, [userToken]);

  // Get WhatsApp templates using the new endpoint
  const getTemplates = async () => {
    setIsLoading(true);
    try {
      const response = await Axios.get('/get-templates', {
        headers: {
          Authorization: userToken
        }
      });
      
      if (response.status === 200) {
        // Update to match the new response structure - templates are in data array
        const templatesData = response.data.message?.data || [];
        setTemplates(templatesData);
        
        // Select first template by default if available
        if (templatesData.length > 0) {
          setSelectedTemplate(templatesData[0]);
        }
      } else {
        // Set empty templates array if no templates or error
        setTemplates([]);
        setSelectedTemplate(null);
      }
    } catch (error) {
      console.error("Error fetching WhatsApp templates:", error);
      setTemplates([]);
      setSelectedTemplate(null);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle template selection
  const handleTemplateSelect = (templateId) => {
    const selected = templates.find(template => template.id === templateId);
    setSelectedTemplate(selected || null);
  };

  // Open modal for creating new template
  const openCreateModal = () => {
    // Reset form fields
    setTemplateName('');
    setTemplateCategory('MARKETING');
    setTemplateLanguage('en_US');
    setTemplateContent('');
    setFormError('');
    setIsModalOpen(true);
  };

  // Close modal
  const closeModal = () => {
    setIsModalOpen(false);
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    if (!templateName.trim()) {
      setFormError('Template name is required');
      return;
    }
    
    // Check for whitespace in template name
    if (/\s/.test(templateName)) {
      setFormError('Template name cannot contain spaces');
      return;
    }
    
    if (!templateContent.trim()) {
      setFormError('Template content is required');
      return;
    }
    
    setIsLoading(true);
    setFormError('');
    
    // Create components structure
    const components = [
      {
        type: "BODY",
        text: templateContent
      }
    ];
    
    try {
      const response = await Axios.post('/create-template', {
        name: templateName.toLowerCase(),
        category: templateCategory,
        text: templateContent,
        
      }, {
        headers: {
          Authorization: userToken,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.status === 200 && response.data.valid) {
        // Refresh templates after creation
        getTemplates();
        closeModal();
      } else {
        setFormError(response.data.message || 'Failed to create template');
      }
    } catch (error) {
      console.error("Error creating WhatsApp template:", error);
      setFormError('Failed to create template. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle template deletion
  const handleDeleteTemplate = async (templateId) => {
    if (!templateId) return;
    
    if (!window.confirm('Are you sure you want to delete this template?')) {
      return;
    }
    
    setIsDeleting(true);
    
    try {
      const response = await Axios.delete(`/whatsapp-template/${templateId}`, {
        headers: {
          Authorization: userToken
        }
      });
      
      if (response.status === 200 && response.data.valid) {
        // Refresh templates after deletion
        getTemplates();
        // If the deleted template was selected, clear selection
        if (selectedTemplate && selectedTemplate.id === templateId) {
          setSelectedTemplate(null);
        }
      }
    } catch (error) {
      console.error("Error deleting WhatsApp template:", error);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <div className="d-md-none">
        <Navbar2 menu={true} />
      </div>
      
      <div className="container mx-auto w-full">
        <div className="flex items-start flex-col md:flex-row">
          <div className="w-full md:w-2/3 md:px-2">
            <div className="py-3 flex justify-between items-center w-full">
              <p className="m-0 p-0 text-[18px] md:text-[24px] text-black font-semibold leading-[22px] md:leading-[36px]">
                WhatsApp Templates
              </p>
              <Button 
                onClick={openCreateModal}
                className="bg-primary hover:bg-primary/90 text-white"
              >
                <Plus className="mr-2 h-4 w-4" /> Create Template
              </Button>
            </div>
            
            <div className="mt-4">
              <Card className="shadow-none">
                <CardContent className="p-0">
                  <div className="flex flex-col lg:flex-row gap-4 p-4">
                    <div className="w-full lg:w-1/3 border-r-0 lg:border-r">
                      <h3 className="text-sm font-medium mb-4">Templates</h3>
                      
                      {isLoading ? (
                        <div className="p-4 text-center">Loading templates...</div>
                      ) : templates.length === 0 ? (
                        <div className="p-4 text-center text-muted-foreground">
                          <AlertCircle className="mx-auto h-8 w-8 mb-2 text-muted-foreground/50" />
                          <p>No templates found</p>
                          <p className="text-sm mt-2">Create your first template to get started</p>
                        </div>
                      ) : (
                        <div className="space-y-2 max-h-[400px] overflow-y-auto pr-2">
                          {templates.map((template) => (
                            <div
                              key={template.id}
                              onClick={() => handleTemplateSelect(template.id)}
                              className={`p-3 rounded-lg cursor-pointer border ${
                                selectedTemplate && selectedTemplate.id === template.id
                                  ? 'border-primary bg-primary/5'
                                  : 'border-border hover:bg-accent'
                              }`}
                            >
                              <div className="flex justify-between items-start">
                                <div>
                                  <p className="font-medium">{template.name}</p>
                                  <p className="text-xs text-muted-foreground mt-1">
                                    {template.parameter_format} • {languages.find(l => l.value === template.language)?.label || template.language}
                                  </p>
                                </div>
                                <Badge variant={template.status === 'APPROVED' ? 'success' : template.status === 'PENDING' ? 'warning' : 'outline'}>
                                  {template.status}
                                </Badge>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    
                    <div className="w-full lg:w-2/3 pl-0 lg:pl-4">
                      {selectedTemplate ? (
                        <div>
                          <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-medium">{selectedTemplate.name}</h3>
                            {/* <div className="flex gap-2">
                              <Button variant="outline" size="sm" className="h-8">
                                <Edit className="h-4 w-4 mr-1" /> Edit
                              </Button>
                              <Button 
                                variant="destructive" 
                                size="sm" 
                                className="h-8"
                                onClick={() => handleDeleteTemplate(selectedTemplate.id)}
                                disabled={isDeleting}
                              >
                                <Trash2 className="h-4 w-4 mr-1" /> Delete
                              </Button>
                            </div> */}
                          </div>
                          
                          <div className="space-y-4">
                            <div>
                              <p className="text-sm font-medium mb-1">Category</p>
                              <p className="text-sm text-muted-foreground">
                                {templateCategories.find(c => c.value === selectedTemplate.category)?.label || selectedTemplate.category}
                              </p>
                            </div>
                            
                            <div>
                              <p className="text-sm font-medium mb-1">Language</p>
                              <p className="text-sm text-muted-foreground">
                                {languages.find(l => l.value === selectedTemplate.language)?.label || selectedTemplate.language}
                              </p>
                            </div>
                            
                            <div>
                              <p className="text-sm font-medium mb-1">Status</p>
                              <Badge variant={selectedTemplate.status === 'APPROVED' ? 'success' : selectedTemplate.status === 'PENDING' ? 'warning' : 'outline'}>
                                {selectedTemplate.status}
                              </Badge>
                            </div>
                            
                            <div>
                              <p className="text-sm font-medium mb-1">Template Content</p>
                              <div className="border rounded-lg p-4 bg-background/50 mt-2">
                                {selectedTemplate.components && selectedTemplate.components.map((component, index) => (
                                  <div key={index} className="mb-3">
                                    <p className="font-medium text-xs text-muted-foreground mb-1">{component.type}</p>
                                    <p className="whitespace-pre-wrap">{component.text}</p>
                                  </div>
                                ))}
                              </div>
                            </div>
                            
                            {selectedTemplate.parameter_format && (
                              <div>
                                <p className="text-sm font-medium mb-1">Parameter Format</p>
                                <p className="text-sm text-muted-foreground">
                                  {selectedTemplate.parameter_format}
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className="h-full flex flex-col items-center justify-center text-center p-6">
                          <h3 className="text-lg font-medium mb-2">No Template Selected</h3>
                          <p className="text-muted-foreground mb-0">
                            Select a template from the list or create a new one to get started
                          </p>
                          {/* <Button onClick={openCreateModal}>
                            <Plus className="mr-2 h-4 w-4" /> Create Template
                          </Button> */}
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
          
          {/* <div className="w-full md:w-1/3 md:px-2 mt-6 md:mt-0">
            <ProfileCompleteCard 
              accountPercentage={accountPercentage}
              fbpagePercentage={fbpagePercentage}
              businessPercentage={businessPercentage}
              notificationPercentage={notificationPercentage}
              overallPercentage={overallPercentage}
            />
          </div> */}
        </div>
      </div>
      
      {/* Create Template Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-background rounded-lg shadow-lg w-full max-w-md p-6 mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Create WhatsApp Template</h3>
              <Button variant="ghost" size="icon" onClick={closeModal}>
                <X className="h-4 w-4" />
              </Button>
            </div>
            
            {formError && (
              <div className="bg-destructive/10 text-destructive text-sm p-3 rounded-md mb-4">
                {formError}
              </div>
            )}
            
            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                <div>
                  <label htmlFor="templateName" className="block text-sm font-medium mb-1">
                    Template Name
                  </label>
                  <Input
                    id="templateName"
                    value={templateName}
                    onChange={(e) => setTemplateName(e.target.value.replace(/\s+/g, '_'))}
                    placeholder="Enter template name"
                    required
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    No spaces allowed (e.g. use hello_world instead of hello world)
                  </p>
                </div>
                
                <div>
                  <label htmlFor="templateCategory" className="block text-sm font-medium mb-1">
                    Category
                  </label>
                  <Select value={templateCategory} onValueChange={setTemplateCategory}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {templateCategories.map((category) => (
                        <SelectItem key={category.value} value={category.value}>
                          {category.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* <div>
                  <label htmlFor="templateLanguage" className="block text-sm font-medium mb-1">
                    Language
                  </label>
                  <Select value={templateLanguage} onValueChange={setTemplateLanguage}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select language" />
                    </SelectTrigger>
                    <SelectContent>
                      {languages.map((language) => (
                        <SelectItem key={language.value} value={language.value}>
                          {language.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div> */}
                
                <div>
                  <label htmlFor="templateContent" className="block text-sm font-medium mb-1">
                    Template Content
                  </label>
                  <Textarea
                    id="templateContent"
                    value={templateContent}
                    onChange={(e) => setTemplateContent(e.target.value)}
                    placeholder="Enter template content"
                    rows={5}
                    required
                  />
                  {/* <p className="text-xs text-muted-foreground mt-1">
                    You can use {'{'}{'{'}'1'{'}'}{'}'},  {'{'}{'{'}'2'{'}'}{'}'}  etc. as placeholders for dynamic content
                  </p> */}
                </div>
                
                <div className="flex justify-end gap-3 mt-6">
                  <Button type="button" variant="outline" onClick={closeModal}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={isLoading}>
                    {isLoading ? 'Creating...' : 'Create Template'}
                  </Button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
      
      <BottomNavbar />
    </>
  );
}

export default WhatsAppTemplate; 